import React, { useState } from 'react';
import { Vendor } from '../types';
import { Search, Plus, Trash2, MapPin, Phone, Mail, Building2, X, Wallet, Package, Pencil } from 'lucide-react';

interface VendorsProps {
  vendors: Vendor[];
  onAddVendor: (vendor: Omit<Vendor, 'id'>) => void;
  onEditVendor: (vendor: Vendor) => void;
  onDeleteVendor: (id: string) => void;
}

const Vendors: React.FC<VendorsProps> = ({ vendors, onAddVendor, onEditVendor, onDeleteVendor }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Omit<Vendor, 'id'>>({
    name: '',
    contact: '',
    email: '',
    address: '',
    payableAmount: 0,
    activeOrders: 0
  });

  const filteredVendors = vendors.filter(v => 
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.contact.includes(searchTerm) ||
    v.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({ name: '', contact: '', email: '', address: '', payableAmount: 0, activeOrders: 0 });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (vendor: Vendor) => {
    setEditingId(vendor.id);
    setFormData({
      name: vendor.name,
      contact: vendor.contact,
      email: vendor.email,
      address: vendor.address,
      payableAmount: vendor.payableAmount,
      activeOrders: vendor.activeOrders
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      onEditVendor({ ...formData, id: editingId });
    } else {
      onAddVendor(formData);
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
             <h2 className="text-lg font-bold text-slate-800">Supplier & Vendor Directory</h2>
             <p className="text-sm text-slate-500">Manage your pharmaceutical suppliers</p>
          </div>
          <div className="flex w-full md:w-auto gap-3">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Search vendors..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>
            <button 
              onClick={handleOpenAdd}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus size={18} />
              Add Vendor
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
           {filteredVendors.map(vendor => (
             <div key={vendor.id} className="bg-slate-50 rounded-xl p-5 border border-slate-200 hover:border-emerald-200 hover:shadow-md transition-all group relative flex flex-col">
                <div className="flex justify-between items-start mb-4">
                   <div className="bg-white p-3 rounded-lg shadow-sm">
                      <Building2 className="text-emerald-600" size={24} />
                   </div>
                   <div className="flex items-center gap-2">
                     <button 
                      onClick={() => handleOpenEdit(vendor)}
                      className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5 bg-white rounded-md border border-slate-200 hover:border-emerald-200"
                      title="Edit Vendor"
                     >
                       <Pencil size={16} />
                     </button>
                     <button 
                      onClick={() => onDeleteVendor(vendor.id)}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1.5 bg-white rounded-md border border-slate-200 hover:border-red-200"
                      title="Delete Vendor"
                     >
                       <Trash2 size={16} />
                     </button>
                   </div>
                </div>
                
                <h3 className="font-bold text-slate-900 text-lg mb-1">{vendor.name}</h3>
                
                <div className="space-y-3 mt-2 mb-4 flex-1">
                   <div className="flex items-center gap-3 text-sm text-slate-600">
                      <Phone size={16} className="text-slate-400 shrink-0" />
                      {vendor.contact}
                   </div>
                   <div className="flex items-center gap-3 text-sm text-slate-600">
                      <Mail size={16} className="text-slate-400 shrink-0" />
                      <span className="truncate">{vendor.email}</span>
                   </div>
                   <div className="flex items-start gap-3 text-sm text-slate-600">
                      <MapPin size={16} className="text-slate-400 shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{vendor.address}</span>
                   </div>
                </div>

                <div className="pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Wallet size={14} />
                      <span className="text-xs font-medium uppercase">Payable</span>
                    </div>
                    <p className={`font-bold text-red-600`}>
                      RS. {vendor.payableAmount.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Package size={14} />
                      <span className="text-xs font-medium uppercase">Orders</span>
                    </div>
                    <p className="font-bold text-slate-900">
                      {vendor.activeOrders} <span className="text-xs font-normal text-slate-400">Active</span>
                    </p>
                  </div>
                </div>
             </div>
           ))}
           
           {filteredVendors.length === 0 && (
             <div className="col-span-full py-12 text-center text-slate-500 flex flex-col items-center">
               <Building2 size={48} className="text-slate-200 mb-3" />
               <p>No vendors found matching your search.</p>
             </div>
           )}
        </div>
      </div>

      {/* Add/Edit Vendor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
               <h3 className="font-bold text-lg text-slate-800">
                 {editingId ? 'Edit Vendor Details' : 'Add New Vendor'}
               </h3>
               <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                 <X size={20} />
               </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                <input 
                  required
                  type="text" 
                  placeholder="e.g. Ali Distributors"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Number</label>
                  <input 
                    required
                    type="text" 
                    placeholder="+92..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formData.contact}
                    onChange={e => setFormData({...formData, contact: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                  <input 
                    required
                    type="email" 
                    placeholder="mail@company.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Address</label>
                <textarea 
                  required
                  placeholder="Office address..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  rows={2}
                  value={formData.address}
                  onChange={e => setFormData({...formData, address: e.target.value})}
                />
              </div>
              
              {/* Financial Overview Section */}
              {editingId && (
                <div className="pt-2 border-t border-slate-100 mt-2">
                   <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">Financial Overview</p>
                   <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Payable (RS.)</label>
                        <input 
                          type="number" 
                          className="w-full px-3 py-2 border border-red-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-red-50/10 text-red-600 font-bold"
                          value={formData.payableAmount}
                          onChange={e => setFormData({...formData, payableAmount: Number(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Active Orders</label>
                        <input 
                          type="number" 
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50 font-bold"
                          value={formData.activeOrders}
                          onChange={e => setFormData({...formData, activeOrders: Number(e.target.value)})}
                        />
                      </div>
                   </div>
                </div>
              )}

              <div className="pt-4 flex gap-3">
                 <button 
                   type="button" 
                   onClick={() => setIsModalOpen(false)}
                   className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium transition-colors"
                 >
                   Cancel
                 </button>
                 <button 
                   type="submit"
                   className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors shadow-sm"
                 >
                   {editingId ? 'Save Changes' : 'Save Vendor'}
                 </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;